import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { getKey } from "pax-ui/core";
import { from, of, throwError } from "rxjs";
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { QuoteMapperService } from "src/app/quote/term-life-insurance/services/backend/mapper/quote-mapper.service";
import { QuoteHttpService } from "src/app/quote/term-life-insurance/services/backend/quote-http.service";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import { QueryStringParameterKey } from "src/app/shared/routes/shared/shared-route.constant";
import { ExceptionResponseDto } from "src/app/shared/services/backend/shared/exceptions/exception-response.dto";
import {
  QueryStringParameter,
  RouterService,
} from "src/app/shared/services/router.service";
import { StoreUpToDateService } from "src/app/shared/services/store-up-to-date.service";
import * as QuoteActions from "src/app/shared/store/quote/term-life-insurance/quote.actions";
import { AppState } from "../../app.state";
import * as QuoteSelectors from "./quote.selectors";
import { QuoteFeatureState } from "./quote.state";

@Injectable()
export class QuoteEffects {
  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _routerService: RouterService,
    private readonly _quoteHttpService: QuoteHttpService,
    private readonly _quoteMapperService: QuoteMapperService,
  ) {}

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Business Actions (those type of actions are initially dispatched)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  submitQuote$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.submitQuote),
      switchMap((action) => {
        const quoteId = this._routerService.tryGetQueryParam(
          QueryStringParameterKey.QUOTE_ID,
        );

        if (!quoteId) {
          return of(
            QuoteActions.createQuoteStart({
              originPage: action.originPage,
              nextNavigationPage: action.nextNavigationPage,
            }),
          );
        }

        if (quoteId) {
          return of(
            QuoteActions.updateQuoteStart({
              quoteId: quoteId,
              originPage: action.originPage,
              nextNavigationPage: action.nextNavigationPage,
            }),
          );
        }

        return throwError(() => {
          return new Error(
            "It should not be possible that we end up here. A backend request to update data should always be either a POST or PATCH request",
          );
        });
      }),
    ),
  );

  getProduct$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getProduct),
      switchMap((action) => {
        return of(QuoteActions.getProductStart(action));
      }),
    ),
  );

  getQuote$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getTermLifeInsuranceQuote),
      withLatestFrom(this._store.select(QuoteSelectors.selectQuoteId)),
      switchMap(([action, existingQuoteId]) => {
        const isQuoteInStoreUpToDate = StoreUpToDateService.isUpToDate(
          existingQuoteId,
          action.quoteId,
        );

        return isQuoteInStoreUpToDate
          ? of(QuoteActions.getQuoteCancel())
          : of(QuoteActions.getQuoteStart(action));
      }),
    ),
  );

  getProductConfiguration$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getProductConfiguration),
      map(() => QuoteActions.getProductConfigurationStart()),
    ),
  );

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Chain Reaction Actions (those type of actions are never directly dispatched but are triggered as a result of a business action)
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  startGetTermLifeInsurance$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getQuoteStart),
      switchMap((action) => {
        return this._quoteHttpService
          .getTermLifeInsuranceConstantQuote(action.quoteId)
          .pipe(
            map((response) => QuoteActions.getQuoteSuccess(response)),
            catchError((error: ExceptionResponseDto) =>
              of(QuoteActions.getQuoteFail(error)),
            ),
          );
      }),
    ),
  );

  startCreateTermLifeInsurance$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.createQuoteStart),
      withLatestFrom(this._store.select(QuoteSelectors.selectFeatureState)),
      switchMap(([action, lifeInsuranceState]) => {
        const requestDto =
          this._quoteMapperService.createCreateQuoteRequest(lifeInsuranceState);
        return this._quoteHttpService
          .createTermLifeInsuranceConstantQuote(requestDto)
          .pipe(
            map((response) =>
              QuoteActions.createQuoteSuccess({
                ...response,
                originPage: action.originPage,
                nextNavigationPage: action.nextNavigationPage,
              }),
            ),
            catchError((error: ExceptionResponseDto) =>
              of(QuoteActions.createQuoteFail(error)),
            ),
          );
      }),
    ),
  );

  startUpdateTermLifeInsurance$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.updateQuoteStart),
      withLatestFrom(this._store.select(QuoteSelectors.selectFeatureState)),
      switchMap(([action, lifeInsuranceState]) => {
        const requestDto =
          this._quoteMapperService.createCreateQuoteRequest(lifeInsuranceState);
        return this._quoteHttpService
          .updateTermLifeInsuranceConstantQuote(action.quoteId, requestDto)
          .pipe(
            map((response) =>
              QuoteActions.updateQuoteSuccess({
                ...response,
                originPage: action.originPage,
                nextNavigationPage: action.nextNavigationPage,
              }),
            ),
            catchError((error: ExceptionResponseDto) =>
              of(QuoteActions.updateQuoteFail(error)),
            ),
          );
      }),
    ),
  );

  startGetProductConfiguration$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getProductConfigurationStart),
      switchMap(() => {
        return this._quoteHttpService
          .getTermLifeInsuranceProductConfiguration()
          .pipe(
            map((response) =>
              QuoteActions.getProductConfigurationSuccess(response),
            ),
            catchError((error: ExceptionResponseDto) =>
              of(QuoteActions.getProductConfigurationFail(error)),
            ),
          );
      }),
    ),
  );

  startGetProduct$ = createEffect(() =>
    this._actions$.pipe(
      ofType(QuoteActions.getProductStart),
      switchMap((action) => {
        return this._quoteHttpService.getProduct(action.quoteId).pipe(
          map((response) => QuoteActions.getProductSuccess(response)),
          catchError((error: ExceptionResponseDto) =>
            of(QuoteActions.getProductFail(error)),
          ),
        );
      }),
    ),
  );

  successProductSpecificQuoteMutations$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          QuoteActions.createQuoteSuccess,
          QuoteActions.updateQuoteSuccess,
        ),
        withLatestFrom(this._store.select(QuoteSelectors.selectFeatureState)),
        concatMap(([action, quoteState]) => {
          if (!quoteState.quoteId) {
            throw new InvalidOperationException(
              `It should not be possible that the '${getKey<QuoteFeatureState>(
                "quoteId",
              )}' is empty. We expect that this should always be the '${getKey<QuoteFeatureState>(
                "quoteId",
              )}' which was returned from the backend`,
            );
          }

          const queryStringParameters = this.createQueryStringParameters(
            quoteState.quoteId,
          );

          if (action.nextNavigationPage) {
            return from(
              this._routerService.navigateToPage(
                action.nextNavigationPage,
                queryStringParameters,
              ),
            );
          } else {
            return from(
              this._routerService.setQueryParamsForCurrentRoute(
                queryStringParameters,
              ),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  private createQueryStringParameters(quoteId: string) {
    const queryStringParameters: QueryStringParameter[] = [
      {
        key: QueryStringParameterKey.QUOTE_ID,
        value: quoteId,
      },
      {
        key: QueryStringParameterKey.ATTRIBUTION_CODE,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_SOURCE,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_MEDIUM,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_CAMPAIGN,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_CONTENT,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_TERM,
        value: null,
      },
      {
        key: QueryStringParameterKey.UTM_ID,
        value: null,
      },
      {
        key: QueryStringParameterKey.GCL_ID,
        value: null,
      },
    ];

    return queryStringParameters;
  }
}
